import { homeRoute, loginRoute, userManagementRoute, clientAdministrationRoute, changePasswordRoute, editProfileRoute, dynamicClientAdministrationRoute, dynamicUserManagementRoute, themeShowcaseRoute, runningTotalsRoute, tripOverviewRoute, statisticEventsRoute, diagnosticEventsRoute, periodicStatisticsRoute, samplingProfilesRoute, vehiclesRoute } from './../../core/routing/route-names';
import { Component, DestroyRef, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterLinkActive, RouterLink } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { UserInfoDTO } from '../models/models.swagger.generated';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ItemModel, DropDownButtonModule } from '@syncfusion/ej2-angular-splitbuttons';
import { ModalService } from '../shared/modal/modal.service';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { ThemeService } from '../services/theme.service';

@Component({
    selector: 'app-navigation',
    templateUrl: './navigation.component.html',
    styleUrls: ['./navigation.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [LanguageSelectorComponent, TranslatePipe, DropDownButtonModule, RouterLink, RouterLinkActive]
})
export class NavigationComponent implements OnInit {
  public navbarOpen = false;
  public loading = false;
  public userInfoModels: UserInfoDTO[] = [];
  public userInfoModels$!: Observable<UserInfoDTO[]>;
  public homeRoute = homeRoute;
  public loginRoute = loginRoute;
  public userManagementRoute = userManagementRoute;
  public clientAdministrationRoute = clientAdministrationRoute;
  public dynamicClientAdministrationRoute = dynamicClientAdministrationRoute;
  public dynamicUserManagementRoute = dynamicUserManagementRoute;
  public themeShowcaseRoute = themeShowcaseRoute;
  public runningTotalsRoute = runningTotalsRoute;
  public tripOverviewRoute = tripOverviewRoute;
  public statisticEventsRoute = statisticEventsRoute;
  public diagnosticEventsRoute = diagnosticEventsRoute;
  public periodicStatisticsRoute = periodicStatisticsRoute;
  public samplingProfilesRoute = samplingProfilesRoute;
  public vehiclesRoute = vehiclesRoute;

  @ViewChild('dropdown') public dropdown: unknown;

  public userIcon = 'icon-user';
  public userNavigation: ItemModel[] = [];

  constructor(
    private router: Router,
    private modalService: ModalService,
    private translate: TranslateService,
    private themeService: ThemeService,
    private readonly destroyRef: DestroyRef,
    public authService: AuthService) {
  }

  public ngOnInit(): void {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildUserNavigation());

    this.themeService.mode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildUserNavigation());
  }

  public get version(): string {
    return environment.version;
  }

  public onSelection(event: any): void {
    switch(event.item.id) {
      case 'editProfile':
        this.editUser();
        break;
      case 'changePassword':
        this.changePassord();
        break;
      case 'logout':
        this.logout();
        break;
      case 'toggleTheme':
        this.toggleTheme();
        break;
    }
  }

  public toggleTheme(): void {
    const currentMode = this.themeService.getCurrentMode();
    const nextMode = currentMode === 'dark' ? 'light' : 'dark';
    this.themeService.setMode(nextMode);
  }

  public editUser() {
    this.router.navigate([editProfileRoute]);
  }

  public logout() {
    const dialog = this.modalService.showOkCancelDialog('i18n.core.modal.logout.title', 'i18n.core.modal.logout.question');
    dialog.onOkSubject.subscribe(() => {
      this.router.navigate([loginRoute], { queryParams: { init: 'true' } })
        .then(() => { this.authService.logout(); });
    });
  }

  public changePassord() {
    this.router.navigate([changePasswordRoute]);
  }

  private buildUserNavigation(): void {
    const toggleThemeText = this.themeService.getCurrentMode() === 'dark'
      ? this.translate.instant('i18n.core.navigation.switchToLightTheme')
      : this.translate.instant('i18n.core.navigation.switchToDarkTheme');
    const toggleThemeIcon =
      this.themeService.getCurrentMode() === "dark"
        ? "icon-light"
        : "icon-asleep";

    this.userNavigation = [
      {
        text: this.translate.instant("i18n.core.navigation.editProfile"),
        id: "editProfile",
        iconCss: "icon-user--profile",
      },
      {
        text: this.translate.instant("i18n.core.navigation.changePassword"),
        id: "changePassword",
        iconCss: "icon-password",
      },
      { text: toggleThemeText, id: "toggleTheme", iconCss: toggleThemeIcon },
      {
        text: this.translate.instant("i18n.core.navigation.logout"),
        id: "logout",
        iconCss: "icon-logout",
      },
      { text: "V" + environment.version, id: "version" },
    ];
  }
}
